import React from 'react';
import { useIntl } from 'react-intl';

import UserDepartmentSelect from '~/components/Shared/UserDepartmentSelect/UserDepartmentSelect';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { EntityType } from '~/enums/EntityType';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { Nullable } from '~/types/Nullable';

import { EntityVisibilityType } from './types';
import { getUserDepartmentArray } from './utils';

type UserDepartmentSelectValue = {
  id: number;
  type: EntityType;
};

interface Props {
  label: string;
  value: EntityVisibilityType;
  onChange: (entityVisibility: EntityVisibilityType) => void;
  enablePortal?: boolean;
}

export function EntityVisibilitySelect({
  label,
  value,
  onChange,
  enablePortal = true,
}: Props) {
  const currentUser = useCurrentUser();
  const intl = useIntl();
  const { visibility } = value;

  const onChangeVisibility = (
    updatedSelectValue: Nullable<
      SaveSearchPermissionType | SaveSearchPermissionType[]
    >,
  ) => {
    let payload;
    if (updatedSelectValue !== SaveSearchPermissionType.Shared) {
      payload = {
        visibility: updatedSelectValue as SaveSearchPermissionType,
      };
    } else {
      payload = {
        ...value,
        visibility: updatedSelectValue as SaveSearchPermissionType,
      };
    }
    onChange(payload);
  };

  const onUserDepartmentChange = (values: UserDepartmentSelectValue[]) => {
    onChange({
      ...value,
      userIds: values.length
        ? values
            .filter((item) => item.type === EntityType.User)
            .map((item) => item.id)
        : undefined,
      departmentIds: values.length
        ? values
            .filter((item) => item.type === EntityType.Department)
            .map((item) => item.id)
        : undefined,
    });
  };

  const getSaveSearchPermissionOptions = (clientName: string) => {
    return [
      {
        label: intl.formatMessage({
          id: 'EntityVisibilitySelect.onlyMePrivate',
          defaultMessage: 'Only me (private)',
          description: 'Label for private visibility option',
        }),
        value: SaveSearchPermissionType.Private,
      },
      {
        label: intl.formatMessage({
          id: 'EntityVisibilitySelect.specificDepartmentsUsers',
          defaultMessage: 'Specific departments (groups) / users',
          description: 'Label for shared visibility option',
        }),
        value: SaveSearchPermissionType.Shared,
      },
      {
        label: intl.formatMessage(
          {
            id: 'EntityVisibilitySelect.anyoneAtClient',
            defaultMessage: 'Anyone at {clientName}',
            description:
              'Label for open visibility option. Example: clientName = "Acme Corp"',
          },
          { clientName },
        ),
        value: SaveSearchPermissionType.Open,
      },
    ];
  };

  return (
    <Layout direction="column" spacing={4}>
      <FormField
        name="visibility-select"
        label={label}
        input={Select}
        onChange={onChangeVisibility}
        value={visibility}
        inputProps={{
          options: getSaveSearchPermissionOptions(currentUser.client_name),
          enablePortal: enablePortal,
          isSearchable: false,
          isClearable: false,
        }}
      />
      {visibility === SaveSearchPermissionType.Shared && (
        <UserDepartmentSelect
          enablePortal={enablePortal}
          value={getUserDepartmentArray(value)}
          onChange={onUserDepartmentChange}
        />
      )}
    </Layout>
  );
}
